import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InputType, SessionSummary } from "@re/shared";
import { api } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  Icon,
  SectionHeading,
  SourceBadge,
  VisibilityPill,
  cn,
  fmtRelative,
} from "../components/ui";

export const Route = createFileRoute("/")({
  component: HomePage,
});

const SOURCE_TYPES: InputType[] = [
  "youtube",
  "arxiv",
  "github",
  "huggingface",
  "blog",
  "community",
  "topic",
];

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const feed = useQuery({ queryKey: ["sessions", "public"], queryFn: () => api.listPublicSessions() });
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), retry: false });
  const loggedIn = me.isSuccess;

  const [filter, setFilter] = useState<InputType | "all">("all");
  const filtered = useMemo(() => {
    if (!feed.data) return [];
    return filter === "all" ? feed.data : feed.data.filter((s) => s.input_type === filter);
  }, [feed.data, filter]);

  const features = [
    {
      icon: "compass" as const,
      eyebrow: t("landing.featuresReadsEyebrow"),
      title: t("landing.featuresReadsTitle"),
      body: t("landing.featuresReadsBody"),
    },
    {
      icon: "beaker" as const,
      eyebrow: t("landing.featuresExpandsEyebrow"),
      title: t("landing.featuresExpandsTitle"),
      body: t("landing.featuresExpandsBody"),
    },
    {
      icon: "book" as const,
      eyebrow: t("landing.featuresSynthEyebrow"),
      title: t("landing.featuresSynthTitle"),
      body: t("landing.featuresSynthBody"),
    },
  ];

  return (
    <main className="max-w-[1320px] mx-auto px-6 pb-20">
      {/* ─── Hero ─── */}
      <section className="pt-16 pb-14 max-w-[860px]">
        <div className="inline-flex items-center gap-2 mb-7">
          <Badge tone="line" size="sm" icon="dot">
            {t("landing.heroBadge")}
          </Badge>
        </div>
        <h1 className="font-serif text-[clamp(40px,5.4vw,68px)] leading-[1.02] tracking-[-0.025em] font-medium text-ink">
          {t("landing.heroTitleA")}
          <br />
          <span className="text-ink-dim italic">{t("landing.heroTitleB")}</span>
        </h1>
        <p className="mt-7 text-[17px] leading-[1.55] text-ink-dim max-w-[640px]">
          {t("landing.heroBody")}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          {loggedIn ? (
            <>
              <Button variant="accent" size="xl" icon="plus" onClick={() => navigate({ to: "/new" })}>
                {t("landing.ctaNew")}
              </Button>
              <Button variant="secondary" size="xl" iconRight="arrow-right" onClick={() => navigate({ to: "/me" })}>
                {t("landing.ctaMine")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" size="xl" iconRight="arrow-right" onClick={() => navigate({ to: "/login" })}>
                {t("landing.ctaGoogle")}
              </Button>
              <Button variant="secondary" size="xl" onClick={() => navigate({ to: "/login" })}>
                {t("landing.ctaApple")}
              </Button>
            </>
          )}
        </div>

        <div className="mt-6 text-[12.5px] text-ink-faint flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <Icon name="check" size={13} /> {t("landing.freeNote")}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1.5">
            <Icon name="lock" size={13} /> {t("landing.proNote")}
          </span>
        </div>
      </section>

      {/* ─── What it does — three columns ─── */}
      <section className="grid md:grid-cols-3 gap-px bg-border border-y border-border mb-20">
        {features.map((f) => (
          <div key={f.title} className="bg-bg p-7">
            <div className="flex items-center gap-2.5 text-ink-dim mb-4">
              <Icon name={f.icon} size={16} />
              <div className="text-micro uppercase tracking-wider font-medium">{f.eyebrow}</div>
            </div>
            <h3 className="font-serif text-[22px] leading-[1.2] font-medium text-ink tracking-tight mb-2">
              {f.title}
            </h3>
            <p className="text-[13.5px] leading-[1.55] text-ink-dim">{f.body}</p>
          </div>
        ))}
      </section>

      {/* ─── Public feed ─── */}
      <section>
        <SectionHeading
          eyebrow={t("landing.feedEyebrow")}
          title={t("landing.feedTitle")}
          action={
            <div className="hidden md:flex items-center gap-1 p-1 bg-surface-2 rounded-md border border-border">
              {(["all", ...SOURCE_TYPES] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    "h-7 px-2.5 rounded-[5px] text-[12px] font-medium transition-colors uppercase tracking-wider",
                    filter === f ? "bg-surface text-ink shadow-sm" : "text-ink-faint hover:text-ink-dim",
                  )}
                  style={{ letterSpacing: "0.05em" }}
                >
                  {f === "all" ? t("landing.filterAll") : t(`source.${f}`)}
                </button>
              ))}
            </div>
          }
        />

        <div className="md:hidden mb-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as InputType | "all")}
            className="h-9 w-full px-3 bg-surface border border-border rounded-md text-[13px] text-ink-dim"
          >
            {(["all", ...SOURCE_TYPES] as const).map((f) => (
              <option key={f} value={f}>
                {f === "all" ? t("landing.filterAll") : t(`source.${f}`)}
              </option>
            ))}
          </select>
        </div>

        {feed.isLoading ? (
          <FeedSkeleton />
        ) : feed.error ? (
          <Card className="p-5 text-[13px] text-[color:var(--danger)]">
            {t("landing.feedLoadFailed", { error: String(feed.error) })}
          </Card>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-ink-faint">
            <Icon name="search" size={24} className="mx-auto mb-3 opacity-50" />
            {t("landing.emptyFeed")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map((s) => (
              <SessionCard key={s.slug} s={s} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

/* ─────────── Session feed card (real SessionSummary) ─────────── */
function SessionCard({ s }: { s: SessionSummary }) {
  const { t, i18n } = useTranslation();
  return (
    <Card
      interactive
      as={Link}
      to="/sessions/$slug"
      params={{ slug: s.slug }}
      className="block p-5 group"
    >
      <div className="flex items-start gap-3 mb-3">
        <SourceBadge type={s.input_type} />
        {s.model && (
          <Badge tone="line" size="sm">
            {s.model}
          </Badge>
        )}
        <div className="flex-1" />
        <div className="text-[12px] text-ink-faint font-mono">{fmtRelative(s.created, i18n.language)}</div>
      </div>
      <h3 className="font-serif text-[20px] leading-[1.25] font-medium text-ink tracking-tight mb-2 group-hover:text-accent-ink transition-colors">
        {s.title}
      </h3>
      {s.intent_purpose && (
        <p className="text-[14px] leading-[1.55] text-ink-dim mb-4 line-clamp-2">{s.intent_purpose}</p>
      )}
      <div className="flex items-center gap-3 pt-3 border-t border-border text-[12px] text-ink-faint font-mono">
        <span>{t("landing.sourceCount", { count: s.source_count })}</span>
        <div className="flex-1" />
        {s.visibility && <VisibilityPill visibility={s.visibility} size="xs" />}
      </div>
    </Card>
  );
}

function FeedSkeleton() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="p-5">
          <div className="shimmer h-5 w-24 rounded mb-3" />
          <div className="shimmer h-6 w-3/4 rounded mb-2" />
          <div className="shimmer h-4 w-full rounded" />
        </Card>
      ))}
    </div>
  );
}
